"use client";

import { useCallback, useEffect, useRef } from "react";

import { useMotionValueEvent, useScroll } from "motion/react";

import Hls from "hls.js";

interface VideoProps extends React.VideoHTMLAttributes<HTMLVideoElement> {
  src: string;
  lazy?: boolean;
  lazyThresholdPx?: number;
  loop?: boolean;
}

function Video({
  src,
  lazy = true,
  lazyThresholdPx = 300,
  loop = true,
  ...props
}: VideoProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const hlsRef = useRef<Hls | null>(null);
  const { scrollY } = useScroll();

  const isHls = src.endsWith(".m3u8");

  const addSrc = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;

    // If video already loaded or hls attached, skip
    if ((video.src && video.src !== "") || hlsRef.current) return;

    const top = video.getBoundingClientRect().top;
    const triggerDistance = window.innerHeight + lazyThresholdPx;

    if (!lazy || top <= triggerDistance) {
      if (isHls && Hls.isSupported()) {
        const hls = new Hls();
        hlsRef.current = hls;
        hls.loadSource(src);
        hls.attachMedia(video);
        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          video.play().catch(() => {});
        });
      } else {
        video.src = src;
        video.load();
        video.play().catch(() => {});
      }
    }
  }, []);

  useMotionValueEvent(scrollY, "change", addSrc);

  useEffect(() => {
    addSrc();

    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, []);

  return (
    <video
      className="bg-black"
      ref={videoRef}
      src={lazy || isHls ? undefined : src}
      preload={lazy ? "none" : "auto"}
      loop={loop}
      muted
      playsInline
      tabIndex={-1}
      {...props}
    />
  );
}

export default Video;
